import { useDebouncedValue } from "@src/appV2/lib/utils/useDebouncedValue";
import { useWorkplaceStarredProfessionals } from "@src/appV2/Professionals/api/starredProfessionals/useWorkplaceStarredProfessionals";
import {
  NearbyWorkerStatusLabel,
  useGetNearbyWorkers,
} from "@src/appV2/Professionals/api/useGetNearbyWorkers";
import {
  useGetWorkerDetailsAtWorkplace,
  WorkerCategory,
  type WorkerDetailsAtWorkplaceSchemaType,
} from "@src/appV2/Professionals/api/useGetWorkerDetailsAtWorkplace";
import { useCallback, useMemo, useState } from "react";

import { type Professional, WORKER_STATUS, type WorkerStatus } from "../types";

/**
 * Normalizes the capitalized wire status label from `useGetNearbyWorkers`
 * into the redesign's internal `WorkerStatus` const-map values. Favorites
 * aren't surfaced here because starred-ness is tracked separately.
 */
function normalizeWorkerStatus(
  label: NearbyWorkerStatusLabel | undefined
): WorkerStatus | undefined {
  if (label === NearbyWorkerStatusLabel.BLOCKED) {
    return WORKER_STATUS.blocked;
  }
  if (label === NearbyWorkerStatusLabel.RESTRICTED) {
    return WORKER_STATUS.restricted;
  }
  return undefined;
}

function getWorkerStatusFromWorkplaceDetails(
  worker: Pick<WorkerDetailsAtWorkplaceSchemaType, "exclusion" | "shiftFeedback">
): WorkerStatus | undefined {
  if (worker.exclusion?.id) {
    return WORKER_STATUS.blocked;
  }
  if (worker.shiftFeedback?.isPositive === false) {
    return WORKER_STATUS.restricted;
  }
  return undefined;
}

const SEARCH_DEBOUNCE_MS = 300;

interface UseProfessionalSearchOptions {
  workplaceId: string;
  qualification: string;
  /** When true, all queries are disabled (e.g. when a professional is already pre-selected). */
  skip?: boolean;
}

interface UseProfessionalSearchReturn {
  searchQuery: string;
  isSearching: boolean;
  starredProfessionals: Professional[];
  recentProfessionals: Professional[];
  searchResults: Professional[];
  allProfessionals: Professional[];
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
}

/**
 * Hook for searching and filtering professionals in the invite drawer.
 *
 * Features:
 * - Combines starred professionals with nearby workers search
 * - Starred professionals always shown first
 * - Search is debounced (300ms)
 * - Results are deduplicated (starred removed from search results)
 */
export function useProfessionalSearch(
  options: UseProfessionalSearchOptions
): UseProfessionalSearchReturn {
  const { workplaceId, qualification, skip = false } = options;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  const {
    isStarredProfessionalEnabled,
    workplaceHasStarredProfessionals,
    workplaceStarredProfessionalWorkerIds,
  } = useWorkplaceStarredProfessionals({ workplaceId: skip ? undefined : workplaceId });

  const { data: starredProfessionalsData, isLoading: isLoadingStarred } =
    useGetWorkerDetailsAtWorkplace(
      {
        category: WorkerCategory.PAST,
        facilityId: workplaceId,
        search: debouncedSearchQuery,
        sort: "-shifts.lastShift.start",
        workerType: qualification,
        workerIds: workplaceStarredProfessionalWorkerIds,
        include: ["exclusion", "shiftFeedback"],
        pageSize: 10,
      },
      {
        enabled: !skip && isStarredProfessionalEnabled && workplaceHasStarredProfessionals,
      }
    );

  const { data: recentPastWorkersData } = useGetWorkerDetailsAtWorkplace(
    {
      category: WorkerCategory.PAST,
      facilityId: workplaceId,
      search: debouncedSearchQuery,
      sort: "-shifts.lastShift.start",
      workerType: qualification,
      include: ["exclusion", "shiftFeedback"],
      pageSize: 10,
    },
    { enabled: !skip }
  );

  const isSearchQueryActive = debouncedSearchQuery.length > 0;

  const { data: allWorkplaceWorkersData, isLoading: isLoadingAllWorkplaceWorkers } =
    useGetWorkerDetailsAtWorkplace(
      {
        category: WorkerCategory.ALL_WORKERS,
        facilityId: workplaceId,
        search: debouncedSearchQuery,
        sort: "name",
        workerType: qualification,
        include: ["exclusion", "shiftFeedback"],
        pageSize: 10,
      },
      {
        enabled: !skip && isSearchQueryActive,
      }
    );

  const { data: nearbyWorkersData, isLoading: isLoadingNearby } = useGetNearbyWorkers(
    {
      workplaceId,
      query: {
        filter: {
          name: isSearchQueryActive ? [debouncedSearchQuery] : [],
          qualification: [qualification],
          excludeDNR: false,
        },
      },
    },
    {
      enabled: !skip && isSearchQueryActive,
    }
  );

  const starredProfessionals = useMemo((): Professional[] => {
    if (!starredProfessionalsData?.workers) {
      return [];
    }

    return starredProfessionalsData.workers.map((worker) => ({
      id: worker.userId,
      name: worker.name,
      avatarUrl: worker.profileImageUrl,
      isStarred: true,
      shiftsWorked: worker.shifts.past,
      statusLabel: getWorkerStatusFromWorkplaceDetails(worker),
    }));
  }, [starredProfessionalsData?.workers]);

  const starredIds = useMemo(
    () => new Set(starredProfessionals.map((p) => p.id)),
    [starredProfessionals]
  );

  const recentProfessionals = useMemo((): Professional[] => {
    if (!recentPastWorkersData?.workers) {
      return [];
    }

    return recentPastWorkersData.workers
      .filter((worker) => !starredIds.has(worker.userId))
      .map((worker) => ({
        id: worker.userId,
        name: worker.name,
        avatarUrl: worker.profileImageUrl,
        isStarred: false,
        shiftsWorked: worker.shifts.past,
        lastShiftDate: worker.shifts.lastShift?.start,
        statusLabel: getWorkerStatusFromWorkplaceDetails(worker),
      }));
  }, [recentPastWorkersData?.workers, starredIds]);

  const searchResults = useMemo((): Professional[] => {
    const workplaceWorkers =
      allWorkplaceWorkersData?.workers?.map((worker) => ({
        id: worker.userId,
        name: worker.name,
        avatarUrl: worker.profileImageUrl,
        isStarred: false,
        shiftsWorked: worker.shifts.past,
        lastShiftDate: worker.shifts.lastShift?.start,
        statusLabel: getWorkerStatusFromWorkplaceDetails(worker),
      })) ?? [];

    const nearbyWorkers =
      nearbyWorkersData?.data.map((worker) => ({
        id: worker.attributes.workerId,
        name: worker.attributes.fullName,
        avatarUrl: worker.attributes.profileImageUrl,
        isStarred: false,
        statusLabel: normalizeWorkerStatus(worker.attributes.statusLabel),
      })) ?? [];

    return [...workplaceWorkers, ...nearbyWorkers]
      .filter((worker) => !starredIds.has(worker.id))
      .filter(
        (worker, index, workers) =>
          workers.findIndex((candidate) => candidate.id === worker.id) === index
      );
  }, [allWorkplaceWorkersData?.workers, nearbyWorkersData?.data, starredIds]);

  const allProfessionals = useMemo((): Professional[] => {
    const recentIds = new Set(recentProfessionals.map((p) => p.id));
    const deduplicatedSearchResults = searchResults.filter((p) => !recentIds.has(p.id));

    return [...starredProfessionals, ...recentProfessionals, ...deduplicatedSearchResults].slice(
      0,
      20
    );
  }, [starredProfessionals, recentProfessionals, searchResults]);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  const isSearching =
    (isLoadingStarred && isStarredProfessionalEnabled && workplaceHasStarredProfessionals) ||
    (isLoadingAllWorkplaceWorkers && isSearchQueryActive) ||
    (isLoadingNearby && isSearchQueryActive);

  return {
    searchQuery,
    isSearching,
    starredProfessionals,
    recentProfessionals,
    searchResults,
    allProfessionals,
    setSearchQuery,
    clearSearch,
  };
}
